import React, { useMemo } from 'react';
import { useField, useFormikContext } from 'formik';
import { Autocomplete } from '@material-ui/lab';
import type { Theme } from '@material-ui/core';
import { Box, TextField } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { useKlpStyles } from '@klp/design/Theme/useKlpStyles';
import { useKostnadsKalkyleContext } from '../../context/KostnadsKalkyleProvider';
import type { Kostnadstype } from '@klp/prosjekt/services/types';
import { IconName, Illustration } from '@klp/design/Illustrations';

const styles = (theme: Theme) => ({
    infoContainer: {
        width: '100%',
        display: 'flex',
        alignItems: 'center',
    },
    heading: {
        fontWeight: 'bold',
        fontSize: '20px',
        marginBottom: '8px',
    },
    buttonContainer: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    formField: {
        '& .MuiTypography-root': {
            color: theme.palette.text.primary,
        },
        '& .Mui-disabled': {
            cursor: 'not-allowed',
        },
        '& .MuiFormLabel-asterisk': {
            color: theme.palette.error.main,
            fontSize: '1.5rem',
        },
    },
});

export const KostnadstypeDropdown = ({
    fieldName,
    rowKey,
    isReadOnly,
}: {
    fieldName: string;
    rowKey: string;
    isReadOnly?: boolean;
}) => {
    const { t } = useTranslation();
    const classes = useKlpStyles({}, styles);
    const { setFieldValue } = useFormikContext();

    const { kostnadstyper, kostnadstyperLoading } = useKostnadsKalkyleContext();

    const [formikField, meta] = useField<Kostnadstype>(fieldName);

    const handleOnChange = (value: Kostnadstype | null) => {
        if (!value) return;

        setFieldValue(fieldName, value);
        setFieldValue(`${rowKey}.prisPrEnhet`, value?.defaultCost);
        setFieldValue(
            `${rowKey}.andelKostnadsforing`,
            value?.defaultKostnadsforingAndel,
        );
    };

    const selectedKostnadstype = useMemo(
        () => kostnadstyper?.find((kt) => kt.id === formikField.value?.id),
        [kostnadstyper, formikField.value?.id],
    );

    const activeKostnadstyper = useMemo(
        () => kostnadstyper?.filter((kt) => !kt.deleted) ?? [],
        [kostnadstyper],
    );

    return (
        <Box>
            <Box className={classes.infoContainer}>
                <Autocomplete
                    id={`${fieldName}-dropdown`}
                    selectOnFocus={true}
                    clearOnBlur={true}
                    fullWidth={true}
                    handleHomeEndKeys={true}
                    options={activeKostnadstyper}
                    getOptionLabel={(option) =>
                        option.kostKategoriId
                            ? `${option.kostKategoriId} - ${option.navn}`
                            : `${option.navn}`
                    }
                    getOptionSelected={(option, value) =>
                        option?.id === value?.id
                    }
                    value={selectedKostnadstype}
                    onChange={(_, value) => handleOnChange(value)}
                    disableClearable={true}
                    loading={kostnadstyperLoading}
                    disabled={isReadOnly}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            disabled={isReadOnly}
                            fullWidth={true}
                            variant="outlined"
                            label={t(
                                'prosjekt.kostnadskalkyle.velgKostnadstype',
                            )}
                            helperText={meta.touched ? meta.error : ''}
                            error={meta.touched && Boolean(meta.error)}
                        />
                    )}
                />
                {!isReadOnly && selectedKostnadstype?.deleted && (
                    <Illustration
                        icon={IconName.ALERT}
                        size="medium"
                        klpPalette="secondary"
                        title={t(
                            'prosjekt.kostnadskalkyle.kostnadstypeWarning',
                        )}
                        marginLeft="compact"
                    />
                )}
            </Box>
        </Box>
    );
};
